// @ts-ignore
import { BaseScene } from '@/ts/utils/webgl/core/baseScene';
// @ts-ignore
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';

// @ts-ignore
import vertexShader from '@/ts/utils/webgl/shaders/mountains/mountains.vert';
// @ts-ignore
import fragmentShader from '@/ts/utils/webgl/shaders/mountains/mountains.frag';

import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { AdditiveBlending, BufferAttribute, BufferGeometry, Color, Euler, MathUtils, MeshStandardMaterial, Points, Quaternion, Raycaster, ShaderMaterial, Vector2, Vector3 } from 'three';

const loader = new GLTFLoader();
let cursor = new Vector2(0, 0);
let position = new Vector3(0, 0, 0);
const rayCaster = new Raycaster();
const cameraPositions: Record<string, [number, number, number]> = window.innerWidth >= 992 ? {
    rest: [34, 6, 1.9],
    center: [29, 5.6, -0.6],
    left: [29, 5.6, 5.6],
    right: [29, 5.6, -5.6],
} : {
    rest: [44, 6, 1],
    center: [29, 5.6, 0],
    left: [29, 5.6, 10],
    right: [29, 5.6, -8],
};

const particlesMaterial = new ShaderMaterial({
    uniforms: {
        time: { value: 0.2 },
        size: { value: 2 }, // Adjust size as needed
        targetPosition: { value: position },
        radius: { value: 1.5 },
        highlightColor: { value: new Color('#99C7F5') },
    },
    vertexShader: vertexShader,
    fragmentShader: fragmentShader,
    transparent: true,
    blending: AdditiveBlending,
    depthWrite: true,
});

//Class that render form a 3D scene, an interactive scene with mountains;
export class MountainScene extends BaseScene {
    id = 'mountainScene';
    background = new Color('black');
    alpha = 0;
    baseActor: Points | null = null;
    particleActors: Record<string, Points | null> = {
        rest: null,
        left: null,
        center: null,
        right: null,
    };
    shouldMorph = false;
    morphTarget: Points | null = null;
    morphCurr: string = 'base';
    morphDuration = 35;
    timerHelper = {
        lastTime: 0,
        morphTimer: this.morphDuration,
    };

    declare _controls: OrbitControls;

    async lights(): Promise<any> {}

    async meshes(): Promise<any> {
        const that = this;

        loader.load(
            '/themes/agid_theme/app/dist/assets/mountains_left.glb',
            function (gltf: any) {
                that.particleActors.left = that.generateParticles(gltf);
            },
            undefined,
            function (error: string) {}
        );
        loader.load(
            '/themes/agid_theme/app/dist/assets/mountains_center.glb',
            function (gltf: any) {
                that.particleActors.center = that.generateParticles(gltf);
            },
            undefined,
            function (error: string) {}
        );
        loader.load(
            '/themes/agid_theme/app/dist/assets/mountains_right.glb',
            function (gltf: any) {
                that.particleActors.right = that.generateParticles(gltf);
            },
            undefined,
            function (error: string) {}
        );

        return new Promise((resolve, reject) => {
            loader.load(
                '/themes/agid_theme/app/dist/assets/mountains_base.glb',
                function (gltf: any) {
                    const particles = that.generateParticles(gltf);
                    that.loadChain.actors.push([particles]);
                    that.loadChain.actors.push([
                        gltf.scene,
                        {
                            id: 'event',
                            effect: () => {
                                that.$canvas.addEventListener('mousemove', ev => {
                                    const x = (ev.clientX / that.$canvas.clientWidth) * 2 - 1;
                                    const y = -(ev.clientY / that.$canvas.clientHeight) * 2 + 1;

                                    cursor.copy({ x, y });
                                    rayCaster.setFromCamera(cursor, that._camera);
                                    const intersects = rayCaster.intersectObjects(gltf.scene.children);
                                    if (intersects.length > 0) {
                                        const intersect = intersects[0];
                                        position.copy(intersect.point);
                                    }
                                });
                            },
                        },
                    ]);
                    that.baseActor = particles;
                    that.particleActors.rest = particles.clone();

                    resolve(true);
                },
                undefined,
                function (error: string) {
                    reject(error);
                }
            );
        });
    }

    async camera(): Promise<any> {
        await super.camera();

        this._camera.position.set(...cameraPositions.rest);
        this._camera.rotation.set(0.7, 1.5, -0.7);
    }

    async controls() {}

    async events(): Promise<any> {
        await super.events();

        this.$target.addEventListener('morphLeft', () => {
            this.setMorph('left');
        });
        this.$target.addEventListener('morphRight', () => {
            this.setMorph('right');
        });
        this.$target.addEventListener('morphCenter', () => {
            this.setMorph('center');
        });
        this.$target.addEventListener('morphRest', () => {
            this.setMorph('rest');
        });
    }

    async gui() {}

    render() {
        super.render();
        if (this.shouldMorph && this.morphTarget) {
            this.morph();
        }
    }

    setTime(stopTime: boolean) {
        if (stopTime) {
            particlesMaterial.uniforms.time.value = null;
        } else {
            particlesMaterial.uniforms.time.value = this.clock.getElapsedTime();
        }
    }

    generateParticles(gltf: any) {
        let pts: any[] = [];
        let v3 = new Vector3();
        gltf.scene.traverse((child: any) => {
            if (child.isMesh) {
                child.updateMatrixWorld(true);
                child.material = new MeshStandardMaterial({
                    color: 0xffffff,
                    wireframe: true,
                    visible: false,
                });

                const inverseQuaternion = new Quaternion().setFromEuler(new Euler(0, 0, 0));

                const positionAttribute = child.geometry.attributes.position;
                for (let i = 0; i < positionAttribute.count; i++) {
                    v3.fromBufferAttribute(positionAttribute, i);
                    v3.applyMatrix4(child.matrixWorld);
                    v3.applyQuaternion(inverseQuaternion);
                    pts.push(v3.clone());
                }

                child.scale.set(10.0, 10.0, 10.0);
                child.position.set(0.0, 0.0, 0.0);
            }
        });

        let bufferGeometry = new BufferGeometry().setFromPoints(pts);

        const particles = new Points(bufferGeometry, particlesMaterial);

        particles.scale.set(10.0, 10.0, 10.0);
        particles.position.set(0.0, 0.0, 0.0);

        return particles;
    }

    morph() {
        const { lastTime, morphTimer } = this.timerHelper;
        let elapsedTime = this.clock.getElapsedTime() - lastTime;
        let t = elapsedTime / morphTimer;
        t = t > 1 ? 1 : t; // Clamp the value of t to ensure it does not exceed 1

        const sourcePositions = this.baseActor!.geometry.attributes.position;
        const destinationPositions = this.morphTarget!.geometry.attributes.position;

        // Create a new geometry that will be used for morphing
        let morphGeometry = new BufferGeometry();
        morphGeometry.setAttribute('position', new BufferAttribute(new Float32Array(sourcePositions.array.length), 3));

        // Interpolate between the source and destination positions
        for (let i = 0; i < sourcePositions.count; i++) {
            let sx = sourcePositions.getX(i);
            let sy = sourcePositions.getY(i);
            let sz = sourcePositions.getZ(i);
            let dx = destinationPositions.getX(i);
            let dy = destinationPositions.getY(i);
            let dz = destinationPositions.getZ(i);

            let ix = MathUtils.lerp(sx, dx, t);
            let iy = MathUtils.lerp(sy, dy, t);
            let iz = MathUtils.lerp(sz, dz, t);

            morphGeometry.attributes.position.setXYZ(i, ix, iy, iz);
        }

        morphGeometry.attributes.position.needsUpdate = true;

        // Update the existing Points object with the new geometry
        this.baseActor!.geometry.dispose();
        this.baseActor!.geometry = morphGeometry;

        //Interpolate Camera Positions
        if (cameraPositions[this.morphCurr]) {
            const [cx, cy, cz] = this._camera.position;
            const [tcx, tcy, tcz] = cameraPositions[this.morphCurr];
            const dcx = MathUtils.lerp(cx, tcx, t);
            const dcy = MathUtils.lerp(cy, tcy, t);
            const dcz = MathUtils.lerp(cz, tcz, t);

            this._camera.position.set(dcx, dcy, dcz);
        }

        if (t >= 0.035) {
            this.shouldMorph = false;
            this.morphTarget = null;
        }
    }

    setMorph(morphPosition: 'rest' | 'left' | 'center' | 'right' = 'rest') {
        if (this.morphCurr !== morphPosition) {
            let time = this.clock.getElapsedTime();
            this.timerHelper = {
                lastTime: time,
                morphTimer: time + this.morphDuration,
            };
            this.shouldMorph = true;
            this.morphTarget = this.particleActors[morphPosition];
            this.morphCurr = morphPosition;
        }
    }
}
